import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Tabs, Tab } from '@mui/material';
import { Waves, TravelExplore, FormatListBulleted } from '@mui/icons-material';
import DiveLogTab from './DiveLogTab';
import DiveSites from './DiveSites';
import DiveLists from './DiveLists';
import AppNavigation from './AppNavigation';
import { authService } from '../services/authService';
import { authService as suuntoAuthService } from '../services/api';
import { diveService } from '../services/diveService';
import { preferencesManager } from '../utils/preferencesManager';
import { usePreferencesSync } from '../hooks/usePreferencesSync';
import { IconButton, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { Logout, Sync, AddLink, UploadFile } from '@mui/icons-material';
import { AuthForm } from './AuthForm';
import { useShareLatestDive } from '../hooks/useShareLatestDive';
import { getSuuntoRedirectUri, openAuthUrl } from '../utils/native';

const TAB_PARAM = 'tab';
const TAB_VALUES = ['log', 'sites', 'lists'] as const;
type TabValue = typeof TAB_VALUES[number];

const DiveExplorer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
  const [user, setUser] = useState(authService.getUser());
  const [suuntoAccessToken, setSuuntoAccessToken] = useState<string | null>(() =>
    localStorage.getItem('suunto_access_token')
  );
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const importInputRef = useRef<HTMLInputElement>(null);

  usePreferencesSync();
  const { offerShare, prompt: sharePrompt } = useShareLatestDive();

  useEffect(() => {
    const currentUser = authService.getUser();
    if (currentUser) setUser(currentUser);
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      preferencesManager.setUserId(user.userId);
    }
  }, [isAuthenticated, user]);

  const getTabFromSearch = (): TabValue => {
    const params = new URLSearchParams(location.search);
    const tab = params.get(TAB_PARAM);
    if (tab === 'sites' || tab === 'lists') return tab;
    return 'log';
  };

  const activeTab = getTabFromSearch();

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    navigate(`/explore?${TAB_PARAM}=${newValue}`, { replace: true });
  };

  const handleAuthSuccess = () => {
    setIsAuthenticated(true);
    const currentUser = authService.getUser();
    if (currentUser) {
      setUser(currentUser);
      preferencesManager.setUserId(currentUser.userId);
      setRefreshKey(prev => prev + 1);
    }
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setSuuntoAccessToken(null);
    localStorage.removeItem('suunto_access_token');
  };

  const handleConnectSuunto = async () => {
    try {
      setSyncMessage(null);
      const redirectUri = getSuuntoRedirectUri();
      const response = await suuntoAuthService.getAuthUrl(redirectUri);

      if (response.authUrl && response.authUrl.includes('your-suunto-client-id-here')) {
        throw new Error('Suunto API credentials are not configured.');
      }

      if (response.authUrl) {
        await openAuthUrl(response.authUrl);
      }
    } catch (error: any) {
      setSyncMessage({ type: 'error', text: error.message || 'Failed to connect to Suunto.' });
    }
  };

  const handleSyncFromSuunto = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      if (!user?.hasSuuntoConnection && suuntoAccessToken) {
        const connectResponse = await diveService.connectSuuntoAccount(suuntoAccessToken);
        if (connectResponse.user) setUser(connectResponse.user);
      }
      const result = await diveService.syncFromSuunto();
      setSyncMessage({
        type: 'success',
        text: `Synced ${result.totalDives} dives (${result.newDives} new, ${result.updatedDives} updated)`,
      });
      setRefreshKey(prev => prev + 1);
      if (result.newDives > 0) void offerShare();
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to sync dives';
      setSyncMessage({ type: 'error', text: errorMessage });
    } finally {
      setSyncing(false);
    }
  };

  const handleImportDiveFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // Allow re-selecting the same file
    if (!file) return;

    setImporting(true);
    setSyncMessage(null);
    try {
      const result = await diveService.importDiveFile(file);
      setSyncMessage({
        type: 'success',
        text: `Imported ${result.newDives} new dive${result.newDives === 1 ? '' : 's'} from ${file.name}` +
          (result.skippedDives > 0 ? ` (${result.skippedDives} already existed)` : ''),
      });
      setRefreshKey(prev => prev + 1);
      if (result.newDives > 0) void offerShare();
    } catch (error: any) {
      setSyncMessage({ type: 'error', text: error.message || 'Failed to import dive file' });
    } finally {
      setImporting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Box>
        <AppNavigation />
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab value="log" icon={<Waves />} label="Dive Log" iconPosition="start" />
          <Tab value="sites" icon={<TravelExplore />} label="Dive Sites" iconPosition="start" />
          <Tab value="lists" icon={<FormatListBulleted />} label="Dive Lists" iconPosition="start" />
        </Tabs>

        {activeTab === 'log' && (
          <Box sx={{ maxWidth: 520, mx: 'auto', mt: { xs: 4, sm: 6 }, mb: 4, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={700} color="primary" gutterBottom>
              Welcome to New Achievement Scuba
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Browse{' '}
              <strong>Dive Sites</strong>,{' '}
              <strong>Training</strong>, and{' '}
              <strong>Flashcards</strong> without an account.
              Sign in to access your personal dive log and sync your Suunto dives.
            </Typography>
            <AuthForm onSuccess={handleAuthSuccess} />
          </Box>
        )}
        {activeTab === 'sites' && <DiveSites />}
        {activeTab === 'lists' && <DiveLists />}
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={2}>
        <Typography variant="h6" color="primary">
          Welcome, {user?.firstName}!
        </Typography>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {(user?.hasSuuntoConnection || suuntoAccessToken) && (
            <IconButton
              onClick={handleSyncFromSuunto}
              disabled={syncing}
              color="primary"
              size="small"
              title={syncing ? 'Syncing…' : 'Sync from Suunto'}
              sx={{ border: 1, borderColor: 'divider' }}
            >
              {syncing ? <CircularProgress size={18} /> : <Sync fontSize="small" />}
            </IconButton>
          )}
          <IconButton
            onClick={handleConnectSuunto}
            color="primary"
            size="small"
            title={user?.hasSuuntoConnection ? 'Reconnect Suunto' : 'Connect Suunto'}
            sx={{ border: 1, borderColor: 'divider' }}
          >
            <AddLink fontSize="small" />
          </IconButton>
          <input
            ref={importInputRef}
            type="file"
            accept=".fit,.uddf,.xml"
            hidden
            onChange={handleImportDiveFile}
          />
          <IconButton
            onClick={() => importInputRef.current?.click()}
            disabled={importing}
            color="primary"
            size="small"
            title={importing ? 'Importing…' : 'Import dive file (FIT or UDDF)'}
            sx={{ border: 1, borderColor: 'divider' }}
          >
            {importing ? <CircularProgress size={18} /> : <UploadFile fontSize="small" />}
          </IconButton>
          <IconButton
            onClick={handleLogout}
            color="error"
            size="small"
            sx={{ border: 1, borderColor: 'divider', '&:hover': { backgroundColor: 'action.hover' } }}
            title="Logout"
          >
            <Logout fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {syncMessage && (
        <Alert
          severity={syncMessage.type}
          sx={{ mb: 2 }}
          onClose={() => setSyncMessage(null)}
          action={
            syncMessage.type === 'error' && /reconnect|expired|connect to suunto/i.test(syncMessage.text) ? (
              <Button color="inherit" size="small" onClick={handleConnectSuunto}>Reconnect</Button>
            ) : undefined
          }
        >
          {syncMessage.text}
        </Alert>
      )}

      <AppNavigation />

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab value="log" icon={<Waves />} label="Dive Log" iconPosition="start" />
        <Tab value="sites" icon={<TravelExplore />} label="Dive Sites" iconPosition="start" />
        <Tab value="lists" icon={<FormatListBulleted />} label="Dive Lists" iconPosition="start" />
      </Tabs>

      {activeTab === 'log' && (
        <DiveLogTab
          key={refreshKey}
          accessToken={null}
          onTokenExpired={() => {}}
          useAccountDives={true}
        />
      )}
      {activeTab === 'sites' && <DiveSites />}
      {activeTab === 'lists' && <DiveLists />}
      {sharePrompt}
    </Box>
  );
};

export default DiveExplorer;
